/**
 * Copyright (c) 2024-2026 Creative Agentics Limited. All rights reserved.
 * Proprietary and confidential. Unauthorised use, reproduction or distribution
 * of this software, in whole or in part, is strictly prohibited.
 * See LICENSE.txt for full terms.
 */

// INDIVIDUAL CLIENT CASES (individual onboarding, 21/10/2026). One test of
// "this case's client is a natural person", used by every individual branch:
// the officer said so at invitation (collected_data.clientKind, written only by
// the invite route and never by a client, api/update-session.js). The MLRO's
// legal form pin is NOT this test, so pinning a company case's legal form
// never moves it onto the individual journey, and nothing changes for a
// company case. Pure.

import { riskConfigForFirm } from './firm-regime.js';
import { samePerson } from './person-name-match.js';

export const isIndividualCase = (cd) => !!(cd && typeof cd === 'object' && String(cd.clientKind || '').toLowerCase() === 'individual');
export const isIndividualSession = (row) => isIndividualCase(row && row.collected_data);

// What the routes say when this version refuses an individual (no dashes: an
// officer reads them).
export const INDIVIDUAL_DOCUMENTS_FIRST_REFUSED = 'Documents first is not available for an individual client in this version. Invite the individual to the interview instead.';
export const INDIVIDUAL_PREFILED_REFUSED = 'Adding documents the firm already holds is not available for an individual client in this version.';
export const INDIVIDUAL_MIGRATE_REFUSED = 'Migrating an individual client is not available in this version.';
export const INDIVIDUAL_ADVISER_REFUSED = 'For an individual client the person invited must be the client themselves. Inviting an adviser or another contact on their behalf is not available in this version.';
export const INDIVIDUAL_REPROCESS_REFUSED = 'An individual client\'s file is built from the interview, not from a documents first run. Use Regenerate instead.';

// The role an individual client carries on the identity and screening lists.
export const CLIENT_PERSON_ROLE = 'Client (natural person)';

// Document thresholds (owner, 21/10/2026): the firm's own values from its
// signed policy questionnaire, else these defaults, in the firm's currency.
// Above sofDocument expected deposits in the first 12 months: one source of
// funds document (a recent bank statement is enough). Above fullSofSow: full
// source of funds and source of wealth documents.
export const INDIVIDUAL_DEFAULT_THRESHOLDS = Object.freeze({ sofDocument: 100000, fullSofSow: 1000000 });

const num = (v) => {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(String(v).replace(/[,\s']/g, ''));
  return Number.isFinite(n) && n >= 0 ? n : null;
};

/**
 * The thresholds that apply, with where each came from (never a silent default:
 * source says 'policy' or 'default'). policy: the firm's stored values
 * ({ sofDocument, fullSofSow, currency }), or null.
 */
export function individualThresholds(policy, firmCurrency) {
  const p = (policy && typeof policy === 'object') ? policy : {};
  const sof = num(p.sofDocument);
  const full = num(p.fullSofSow);
  const currency = /^[A-Z]{3}$/.test(String(p.currency || '')) ? p.currency : (/^[A-Z]{3}$/.test(String(firmCurrency || '')) ? firmCurrency : 'GBP');
  let lo = sof != null ? sof : INDIVIDUAL_DEFAULT_THRESHOLDS.sofDocument;
  let hi = full != null ? full : INDIVIDUAL_DEFAULT_THRESHOLDS.fullSofSow;
  let source = (sof != null || full != null) ? 'policy' : 'default';
  // A policy whose lower figure is above its upper one cannot be applied as
  // written: the stricter reading is taken (both at the lower figure, so
  // everything above it gets full documents) and the source says so.
  if (lo > hi) { lo = hi = Math.min(lo, hi); source = 'policy_invalid'; }
  return { sofDocument: lo, fullSofSow: hi, currency, source };
}

/** The amount band a declared figure falls in against the thresholds: 'none', 'sof_document' or 'full'. */
export function thresholdBand(amount, t) {
  const a = num(amount);
  if (a == null || !t) return null;
  if (a > t.fullSofSow) return 'full';
  if (a > t.sofDocument) return 'sof_document';
  return 'none';
}

/**
 * The regime the case is under: the holding entity's (a multi entity firm,
 * onboarding_sessions.firm_entity), else the firm's (riskConfigForFirm, the
 * same resolution the risk engine cites). 'CH', 'UK', 'EU', 'MU', 'CA', the
 * uncited marker, or null when the firm has none set.
 */
export function caseRegime(firm, entityCode = null) {
  const rc = riskConfigForFirm(firm, entityCode || null);
  return rc && rc.regime ? String(rc.regime) : null;
}

/** Owner, 21/10/2026: Form A in Sumsub for every individual client under the Swiss regime, none elsewhere. */
export const individualNeedsFormA = (firm, entityCode = null) => caseRegime(firm, entityCode) === 'CH';

// The regimes this platform cites and knows are not the Swiss one.
const NON_SWISS = new Set(['UK', 'EU', 'CA', 'MU']);
/**
 * Whether an individual's Form A is required, three ways (review of pieces 4
 * to 6, finding S1): 'required' (Swiss regime), 'not_required' (another
 * regime the platform cites), or 'unknown' (no regime set, an uncited one, or
 * a multi entity firm's case with no holding entity). Unknown is never read as
 * not required: the callers say so on the case and show the form.
 */
export function individualFormAState(firm, entityCode = null) {
  const r = caseRegime(firm, entityCode);
  if (r === 'CH') return 'required';
  if (NON_SWISS.has(r)) return 'not_required';
  return 'unknown';
}

// The thresholds' currency when the firm's policy names none: the regime's own.
const REGIME_CURRENCY = { CH: 'CHF', UK: 'GBP', EU: 'EUR', CA: 'CAD', MU: 'USD' };

/** The thresholds for a firm's individual clients (policy values, else the defaults in the regime's currency). */
export function thresholdsForFirm(firm, entityCode = null) {
  const policy = firm && firm.risk_config && firm.risk_config.individualThresholds;
  return individualThresholds(policy, REGIME_CURRENCY[caseRegime(firm, entityCode)] || 'GBP');
}

/**
 * AN INDIVIDUAL'S BOARD, MADE SAFE TO READ (interview submit and officer
 * regenerate). Whatever the model wrote, the board says the client is a
 * natural person, names them, lists no directors, owners or signatories (the
 * client is their own beneficial owner; anyone else involved is recorded on
 * clientPerson.thirdPartyInvolvement, which the checks read), and puts the
 * client on the identity list with the invitation's email. Returns the board.
 * contact: { name, email } from the invitation.
 */
// A schema placeholder ("...") or blank is no value.
const realText = (v) => { const t = String(v == null ? '' : v).trim(); return (!t || /^[.\u2026\s]+$/.test(t)) ? '' : t; };

export function normaliseIndividualBoard(board, contact = {}, opts = {}) {
  const b = (board && typeof board === 'object' && !Array.isArray(board)) ? board : {};
  const cp = (b.clientPerson && typeof b.clientPerson === 'object') ? { ...b.clientPerson } : {};
  const fullName = realText(cp.fullName) || realText(b.clientName) || realText(contact.name);
  cp.fullName = fullName || null;
  b.clientKind = 'individual';
  b.clientPerson = cp;
  b.clientName = fullName || null;
  b.contactName = realText(b.contactName) || contact.name || null;
  b.contactEmail = contact.email || b.contactEmail || null;
  b.companyDetails = { ...(b.companyDetails && typeof b.companyDetails === 'object' ? b.companyDetails : {}), legalName: fullName || null, legalForm: 'Natural person' };
  for (const k of ['registrationNumber', 'dateOfIncorporation', 'jurisdiction', 'registeredAddress']) delete b.companyDetails[k];
  b.directors = [];
  b.ubos = [];
  b.authorisedSignatories = [];
  // keepListed (an officer's save, review finding S5): the officer's own
  // identity list stands beside the client, and an email they set on the
  // client's entry is kept. A model written board never keeps its list.
  const listed = (opts && opts.keepListed && Array.isArray(b.sumsubRequired)) ? b.sumsubRequired.filter(p => p && typeof p === 'object' && realText(p.name)) : [];
  const own = fullName ? listed.find(p => samePerson(p.name, fullName)) : null;
  const email = (own && realText(own.email)) || contact.email || b.contactEmail || null;
  const others = listed.filter(p => p !== own && !(fullName && samePerson(p.name, fullName)));
  b.sumsubRequired = fullName ? [{ name: fullName, role: CLIENT_PERSON_ROLE, email }, ...others] : others;
  return b;
}

// Major currencies the thresholds can be compared across without a rate feed.
const COMPARABLE = new Set(['GBP', 'EUR', 'USD', 'CHF', 'CAD']);
// Another major currency is compared with this margin added, so an amount near
// a threshold is read as above it (asks for more, never less).
const CROSS_CURRENCY_MARGIN = 1.25;

export const INDIVIDUAL_SOF_GAP = 'A recent bank statement from the account your money will come from, showing where those funds come from.';
export const INDIVIDUAL_SOW_GAP = 'Documents showing how your wealth was built, for example payslips or an employment contract, a sale agreement, a grant of probate, or investment statements, one for each main source of wealth you described.';
export const INDIVIDUAL_SOF_FULL_GAP = 'Documents showing where the money for this relationship comes from, for example bank statements covering the funds, or a sale or completion statement.';

/**
 * THE DOCUMENTS RULE, APPLIED IN CODE (review of pieces 1 to 3, finding 4):
 * the thresholds never reach Alex or the client, so the client cannot be told
 * what amount avoids a document. The band is read from the declared expected
 * total for the first twelve months against the firm's thresholds, the money
 * document gaps are written here in plain wording that names no amount, and
 * the outcome is recorded on the board for the officer
 * (board.individualDocumentRule). An amount that cannot be compared (missing,
 * or in a currency outside the comparable set) is never read as below the
 * threshold: the band is 'undetermined', one source of funds document is
 * asked for, and the record says the officer must judge it. Returns the board.
 */
export function applyIndividualDocumentRule(board, t) {
  const b = (board && typeof board === 'object') ? board : {};
  const th = t || individualThresholds(null, null);
  const da = (b.declaredAmounts && typeof b.declaredAmounts === 'object') ? b.declaredAmounts : {};
  const amount = num(da.expectedFirst12MonthsAmount);
  const cur = String(da.currency || '').trim().toUpperCase();
  let band, basis;
  if (amount == null) { band = 'undetermined'; basis = 'no expected total for the first twelve months was recorded'; }
  else if (cur === th.currency) { band = thresholdBand(amount, th); basis = 'same currency'; }
  else if (COMPARABLE.has(cur)) { band = thresholdBand(amount * CROSS_CURRENCY_MARGIN, th); basis = 'declared in ' + cur + ', compared with a 25 percent margin, no exchange rate'; }
  else { band = 'undetermined'; basis = cur ? 'declared in ' + cur + ', which cannot be compared with ' + th.currency : 'no currency was recorded for the expected total'; }
  let want = band === 'full' ? [INDIVIDUAL_SOF_FULL_GAP, INDIVIDUAL_SOW_GAP] : (band === 'sof_document' || band === 'undetermined') ? [INDIVIDUAL_SOF_GAP] : [];
  // One bank statement already received covers the single document band (only
  // that band: the full band is the officer's to judge against what is on file).
  const received = [...(Array.isArray(b.documentsReceived) ? b.documentsReceived : []), ...(Array.isArray(b.uploadedDocuments) ? b.uploadedDocuments.map(d => d && d.name) : [])].map(x => String(x || '').toLowerCase());
  const statementOnFile = received.some(n => /bank statement|account statement/.test(n));
  if (band !== 'full' && statementOnFile) want = [];
  const ours = new Set([INDIVIDUAL_SOF_GAP, INDIVIDUAL_SOW_GAP, INDIVIDUAL_SOF_FULL_GAP]);
  const others = (Array.isArray(b.documentGaps) ? b.documentGaps : []).filter(g => typeof g === 'string' && !ours.has(g));
  b.documentGaps = [...others, ...want];
  b.individualDocumentRule = { band, amount, currency: cur || null, basis, thresholds: { sofDocument: th.sofDocument, fullSofSow: th.fullSofSow, currency: th.currency, source: th.source }, gapsAdded: want.length, statementOnFile, at: new Date().toISOString() };
  return b;
}

/**
 * The thresholds the dashboard applies on a Regenerate, from what get-firm
 * sent: the case's holding entity's, else the firm's; when neither arrived,
 * the defaults marked 'firm_unread' (never a quiet default).
 */
export function dashboardThresholds(firmData, entityCode) {
  const be = firmData && firmData.individualByEntity;
  const e = entityCode && be && be[entityCode];
  if (e && e.thresholds) return e.thresholds;
  if (firmData && firmData.individualThresholds) return firmData.individualThresholds;
  return { ...individualThresholds(null, null), source: 'firm_unread' };
}

/**
 * A board's clientKind is the invitation's, never the model's (review of
 * pieces 4 to 6, finding S2): the party list and the Form A pre fill read the
 * board, so a company board that came back with clientKind 'individual' must
 * not turn its company into a person. Removes the key from any board whose
 * case is not an individual's. Returns the board.
 */
export function keepClientKindHonest(board, cd) {
  if (board && typeof board === 'object' && !isIndividualCase(cd) && Object.prototype.hasOwnProperty.call(board, 'clientKind')) delete board.clientKind;
  return board;
}

/** The Form A state from what get-firm sent (true, false or null per entity): 'required', 'not_required' or 'unknown'. */
export function individualFormAStateOf(firmData, entityCode) {
  const be = firmData && firmData.individualByEntity;
  const e = entityCode && be && be[entityCode];
  const v = (e && Object.prototype.hasOwnProperty.call(e, 'formA')) ? e.formA : (firmData ? firmData.individualFormA : null);
  return v === true ? 'required' : v === false ? 'not_required' : 'unknown';
}

/**
 * The risk engine's natural person input (CID-RM-1.5), from an individual's
 * board: what the client declared about politically exposed persons, whether
 * they act for themselves, and whether the money comes from their own account.
 * Unknowns stay undefined (the engine rates only what was declared).
 */
export function naturalPersonInput(board) {
  const b = (board && typeof board === 'object') ? board : {};
  const cp = (b.clientPerson && typeof b.clientPerson === 'object') ? b.clientPerson : {};
  const pd = (cp.pepDeclaration && typeof cp.pepDeclaration === 'object') ? cp.pepDeclaration : {};
  const sof = (b.sofAssessment && typeof b.sofAssessment === 'object') ? b.sofAssessment : {};
  return {
    declaredPep: pd.isPep === true,
    declaredPepFamily: pd.familyOrAssociateIsPep === true,
    pepRole: [realText(pd.role), realText(pd.country)].filter(Boolean).join(', ') || null,
    actingForThemselves: typeof cp.actingForThemselves === 'boolean' ? cp.actingForThemselves : undefined,
    thirdParty: realText(cp.thirdPartyInvolvement) || null,
    accountInOwnName: typeof sof.accountInOwnName === 'boolean' ? sof.accountInOwnName : undefined,
  };
}
